import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../services/api";

function Index() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [employees, setEmployees] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [paymentData, setPaymentData] = useState({
    amount: "",
    paid_by: "",
    note: "",
    currency: "",
    exchange_rate: 1,
  });
  const [paymentError, setPaymentError] = useState("");
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 6;

  // Currency symbol function - moved to top
  const getCurrencySymbol = (currencyCode) => {
    const symbols = {
      'USD': '$',
      'AFN': '؋',
      'EUR': '€'
    };
    return symbols[currencyCode] || currencyCode;
  };

  const fetchProjects = async (page = 1) => {
    setLoading(true);
    try {
      const res = await api.get(`projects/?page=${page}&page_size=${itemsPerPage}`);
      console.log("API Response:", res.data);
      
      if (res.data.results !== undefined) {
        setProjects(res.data.results);
        setTotalPages(Math.ceil(res.data.count / itemsPerPage));
        setTotalCount(res.data.count);
      } else if (res.data.count !== undefined) {
        setProjects(res.data.data || res.data.items || []);
        setTotalPages(Math.ceil(res.data.count / itemsPerPage));
        setTotalCount(res.data.count);
      } else {
        const allProjects = res.data;
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedProjects = allProjects.slice(startIndex, endIndex);
        
        setProjects(paginatedProjects);
        setTotalPages(Math.ceil(allProjects.length / itemsPerPage));
        setTotalCount(allProjects.length);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load projects.");
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await api.get("employees/");
      setEmployees(res.data);
    } catch (err) {
      console.error("Failed to fetch employees:", err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this project?")) return;
    try {
      await api.delete(`projects/${id}/`);
      fetchProjects(currentPage);
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete project.");
    }
  };

  const openPaymentModal = (project) => {
    setSelectedProject(project);
    setPaymentData({
      amount: "",
      paid_by: "",
      note: "",
      currency: project.currency || "",
      exchange_rate: project.exchange_rate || 1,
    });
    setPaymentError("");
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closePaymentModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
    setPaymentError("");
    setPaymentLoading(false);
    document.body.style.overflow = 'unset';
  };

  const handlePaymentChange = (e) => {
    setPaymentData({
      ...paymentData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setPaymentError("");
    setPaymentLoading(true);

    if (!paymentData.amount) {
      setPaymentError("Amount is required.");
      setPaymentLoading(false);
      return;
    }

    const amount = parseFloat(paymentData.amount);
    const remaining = parseFloat(selectedProject.remaining || selectedProject.budget);

    if (amount > remaining) {
      setPaymentError(`Amount cannot exceed remaining balance of ${getCurrencySymbol(selectedProject.currency)}${remaining.toFixed(2)}`);
      setPaymentLoading(false);
      return;
    }

    if (amount <= 0) {
      setPaymentError("Amount must be greater than 0.");
      setPaymentLoading(false);
      return;
    }

    if (!paymentData.currency) {
      setPaymentError("Currency is required.");
      setPaymentLoading(false);
      return;
    }

    if (paymentData.currency !== 'AFN' && (!paymentData.exchange_rate || paymentData.exchange_rate <= 0)) {
      setPaymentError("Exchange rate is required for non-AFN currencies.");
      setPaymentLoading(false);
      return;
    }

    try {
      await api.post("payments/", {
      project: selectedProject.id,
      paid_by: paymentData.paid_by ? parseInt(paymentData.paid_by) : null,
      amount: amount,
      currency: paymentData.currency,
      exchange_rate: paymentData.currency !== 'AFN' ? parseFloat(paymentData.exchange_rate) : 1,
      note: paymentData.note || "",
    });
      closePaymentModal();
      fetchProjects(currentPage);
      alert("Payment added successfully!");
    } catch (err) {
      console.error("Payment creation error:", err);
      if (err.response && err.response.data) {
        const serverError = 
          err.response.data.amount || 
          err.response.data.detail || 
          "Failed to add payment.";
        setPaymentError(serverError.toString());
      } else {
        setPaymentError("Network or server error. Please check your connection.");
      }
    } finally {
      setPaymentLoading(false);
    }
  };

  const getProgressPercentage = (project) => {
    const totalPaid = parseFloat(project.total_paid || 0);
    const budget = parseFloat(project.budget || 1);
    return Math.min((totalPaid / budget) * 100, 100);
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 100) return "bg-gradient-to-r from-green-500 to-green-600";
    if (percentage >= 75) return "bg-gradient-to-r from-blue-500 to-blue-600";
    if (percentage >= 50) return "bg-gradient-to-r from-yellow-500 to-yellow-600";
    return "bg-gradient-to-r from-red-500 to-red-600";
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const renderPaginationButtons = () => {
    if (totalPages <= 1) return null;

    const buttons = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    buttons.push(
      <button
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        ←
      </button>
    );

    // First page
    if (startPage > 1) {
      buttons.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          1
        </button>
      );
      if (startPage > 2) {
        buttons.push(
          <span key="ellipsis1" className="px-2 py-1 text-sm text-gray-500">
            ...
          </span>
        );
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 text-sm border rounded-lg ${
            currentPage === i
              ? "bg-purple-500 text-white border-purple-500"
              : "border-gray-300 hover:bg-gray-50"
          }`}
        >
          {i}
        </button>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(
          <span key="ellipsis2" className="px-2 py-1 text-sm text-gray-500">
            ...
          </span>
        );
      }
      buttons.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          {totalPages}
        </button>
      );
    }

    // Next button
    buttons.push(
      <button
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        →
      </button>
    );

    return buttons;
  };

  useEffect(() => {
    fetchProjects(currentPage);
    fetchEmployees();
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [currentPage]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
    </div>
  );
  
  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
      <p className="text-red-600 text-lg">{error}</p>
    </div>
  );

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount);

  return (
    <div className="min-h-screen bg-gray-50/30">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Projects</h1>
          <p className="text-gray-600 text-sm mt-1">
            {totalCount > itemsPerPage ? `Showing ${startItem}-${endItem} of ${totalCount} projects` : `Showing ${totalCount} project${totalCount !== 1 ? 's' : ''}`}
          </p>
        </div>
        <Link
          to="/projects/create"
          className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 transition-colors duration-200 flex items-center space-x-2 text-sm font-medium"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
          </svg>
          <span>Add Project</span>
        </Link>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {projects.length === 0 ? (
          <div className="col-span-full bg-white rounded-2xl shadow-sm border border-gray-200/60 p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No projects yet</h3>
            <p className="text-gray-500 mb-4">Get started by creating your first project</p>
            <Link
              to="/projects/create"
              className="inline-flex items-center space-x-2 text-purple-600 hover:text-purple-700 font-medium"
            >
              <span>Create Project</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
              </svg>
            </Link>
          </div>
        ) : (
          projects.map((project) => {
            const progressPercentage = getProgressPercentage(project);
            const progressColor = getProgressColor(progressPercentage);
            
            return (
              <div key={project.id} className="group bg-white rounded-2xl shadow-sm border border-gray-200/60 overflow-hidden hover:shadow-xl transition-all duration-300 hover:border-purple-200/80">
                {/* Header */}
                <div className="p-6 border-b border-gray-200/60">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-semibold text-gray-900 truncate pr-2 group-hover:text-gray-800 transition-colors">
                      {project.title}
                    </h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium shrink-0 transition-colors ${
                      progressPercentage >= 100 
                        ? "bg-green-100 text-green-800 border border-green-200" 
                        : "bg-blue-100 text-blue-800 border border-blue-200"
                    }`}>
                      {progressPercentage >= 100 ? "Completed" : "Active"}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    Customer: <span className="font-medium text-gray-800">{project.customer?.name || project.customer}</span>
                  </p>
                  <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">
                    {project.description || "No description provided"}
                  </p>
                </div>

                {/* Progress Bar - Compact */}
                <div className="px-6 py-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-medium text-gray-600">Progress</span>
                    <span className="text-xs font-semibold text-gray-900">
                      {progressPercentage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5 mb-1">
                    <div 
                      className={`h-1.5 rounded-full ${progressColor} transition-all duration-500`}
                      style={{ width: `${progressPercentage}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{getCurrencySymbol(project.currency)}{parseFloat(project.total_paid || 0).toFixed(2)} paid</span>
                    <span>{getCurrencySymbol(project.currency)}{parseFloat(project.budget).toFixed(2)} total</span>
                    {console.log(project.currency)}
                  </div>
                </div>

                {/* Financial Info */}
                <div className="px-6 py-4 bg-white">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="text-center p-3 bg-gray-50 rounded-xl border border-gray-200/60">
                      <div className="text-gray-600 text-xs font-medium mb-1">Budget</div>
                      <p className="font-bold text-gray-900 text-lg">{getCurrencySymbol(project.currency)}{parseFloat(project.budget).toFixed(2)}</p>
                    </div>
                    <div className="text-center p-3 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200/60">
                      <div className="text-gray-600 text-xs font-medium mb-1">Remaining</div>
                      <p className="font-bold text-green-700 text-lg">
                        {getCurrencySymbol(project.currency)}{parseFloat(project.remaining || project.budget).toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 text-xs text-gray-500 flex justify-between items-center bg-gray-50/50 p-2 rounded-lg">
                    <div className="flex items-center space-x-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                      </svg>
                      <span>Start: {project.start_date ? new Date(project.start_date).toLocaleDateString() : "-"}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                      </svg>
                      <span>End: {project.end_date ? new Date(project.end_date).toLocaleDateString() : "-"}</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="px-6 py-4 bg-gray-50/30 border-t border-gray-200/60">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Link
                        to={`/projects/edit/${project.id}`}
                        className="text-blue-600 hover:text-blue-700 p-2 rounded-xl hover:bg-blue-50 transition-all duration-200 border border-transparent hover:border-blue-200"
                        title="Edit Project"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                        </svg>
                      </Link>

                      <Link
                        to={`/projects/contract/${project.id}`}
                        className="text-purple-600 hover:text-purple-700 p-2 rounded-xl hover:bg-purple-50 transition-all duration-200 border border-transparent hover:border-purple-200"
                        title="Generate Contract"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                        </svg>
                      </Link>
                      
                      <button
                        onClick={() => handleDelete(project.id)}
                        className="text-red-600 hover:text-red-700 p-2 rounded-xl hover:bg-red-50 transition-all duration-200 border border-transparent hover:border-red-200"
                        title="Delete Project"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                        </svg>
                      </button>
                    </div>
                    <button
                      onClick={() => openPaymentModal(project)}
                      disabled={!project || !project.id || parseFloat(project.remaining || project.budget) <= 0}
                      className={`px-5 py-2.5 rounded-xl font-semibold transition-all duration-300 text-sm flex items-center space-x-2 group/btn ${
                        !project || !project.id || parseFloat(project.remaining || project.budget) <= 0
                          ? "bg-gray-300 cursor-not-allowed text-gray-500"
                          : "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-md hover:shadow-lg hover:scale-105"
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"/>
                      </svg>
                      <span>{parseFloat(project.remaining || project.budget) <= 0 ? "Fully Paid" : "Add Payment"}</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-gray-600">
            Showing {startItem}-{endItem} of {totalCount} projects
          </div>
          <div className="flex items-center space-x-1 flex-wrap justify-center">
            {renderPaginationButtons()}
          </div>
        </div>
      )}

      {/* Enhanced Payment Modal - Responsive */}
      {isModalOpen && selectedProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
          {/* Backdrop with blur */}
          <div 
            className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300"
            onClick={closePaymentModal}
          />
          
          {/* Modal Container - Responsive sizing */}
          <div className="relative w-full max-w-[95vw] sm:max-w-md md:max-w-lg transform transition-all duration-300 scale-100 opacity-100">
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden border border-gray-200/80 max-h-[90vh] sm:max-h-[85vh] flex flex-col">
              {/* Header */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100/70 border-b border-gray-200/60 p-4 sm:p-6 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-md">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"/>
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                        Add Payment
                      </h2>
                      <p className="text-xs sm:text-sm text-gray-600 mt-0.5">Record payment for project</p>
                    </div>
                  </div>
                  <button
                    onClick={closePaymentModal}
                    disabled={paymentLoading}
                    className="text-gray-400 hover:text-gray-600 p-1 sm:p-2 rounded-lg hover:bg-white/80 transition-all duration-200 disabled:opacity-50"
                  >
                    <svg className="w-5 h-5 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
              </div>

              {/* Project Info */}
              <div className="p-4 sm:p-6 border-b border-gray-200/60 flex-shrink-0">
                <div className="bg-gradient-to-r from-blue-50/80 to-indigo-50/80 border border-blue-200/60 rounded-xl sm:rounded-2xl p-3 sm:p-4 backdrop-blur-sm">
                  <div className="flex items-start justify-between mb-2 sm:mb-3">
                    <h3 className="font-semibold text-blue-900 text-xs sm:text-sm truncate flex-1 mr-2">
                      {selectedProject.title}
                    </h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium shrink-0 border ${
                      getProgressPercentage(selectedProject) >= 100 
                        ? "bg-green-100 text-green-800 border-green-200" 
                        : "bg-blue-100 text-blue-800 border-blue-200"
                    }`}>
                      {getProgressPercentage(selectedProject) >= 100 ? "Paid" : "In Progress"}
                    </span>
                  </div>
                  
                  {/* Budget Progress */}
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex justify-between text-xs text-blue-700 font-medium">
                      <span>Budget: {getCurrencySymbol(selectedProject.currency)}{parseFloat(selectedProject.budget).toFixed(2)}</span>
                      <span>Remaining: {getCurrencySymbol(selectedProject.currency)}{parseFloat(selectedProject.remaining || selectedProject.budget).toFixed(2)}</span>
                    </div>
                    
                    {/* Mini Progress Bar - Compact */}
                    <div className="w-full bg-blue-200/50 rounded-full h-1.5">
                      <div 
                        className={`h-1.5 rounded-full transition-all duration-700 ${getProgressColor(getProgressPercentage(selectedProject))}`}
                        style={{ width: `${getProgressPercentage(selectedProject)}%` }}
                      ></div>
                    </div>
                    
                    <div className="flex justify-between text-xs text-blue-600">
                      <span>Paid: {getCurrencySymbol(selectedProject.currency)}{parseFloat(selectedProject.total_paid || 0).toFixed(2)}</span>
                      <span className="font-semibold">{getProgressPercentage(selectedProject).toFixed(1)}% Complete</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Form - Scrollable area */}
              <div className="p-4 sm:p-6 overflow-y-auto custom-scrollbar flex-1">
                <form onSubmit={handlePaymentSubmit} className="space-y-4 sm:space-y-5">
                  {/* Error Message */}
                  {paymentError && (
                    <div className="bg-red-50 border border-red-200 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                      <div className="flex items-start">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 mr-2 sm:mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                        <p className="text-red-700 text-sm font-medium">{paymentError}</p>
                      </div>
                    </div>
                  )}

                  {/* Currency Field */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2 sm:mb-3">
                      Currency *
                    </label>
                    <select
                      name="currency"
                      value={paymentData.currency}
                      onChange={handlePaymentChange}
                      className="w-full border border-gray-300 rounded-xl sm:rounded-2xl p-3 sm:p-4 focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all duration-200 bg-white shadow-sm"
                      required
                      disabled={paymentLoading}
                    >
                      <option value="">Select Currency</option>
                      <option value="USD">US Dollar (USD)</option>
                      <option value="AFN">Afghan Afghani (AFN)</option>
                      <option value="EUR">Euro (EUR)</option>
                    </select>
                  </div>

                  {/* Exchange Rate Field - Only show if currency is NOT USD */}
                  {paymentData.currency && paymentData.currency !== 'AFN' && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2 sm:mb-3">
                        Exchange Rate *
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm sm:text-base">1 {paymentData.currency} =</span>
                        <input
                          type="number"
                          name="exchange_rate"
                          value={paymentData.exchange_rate}
                          onChange={handlePaymentChange}
                          step="0.000001"
                          min="0.000001"
                          className="w-full border border-gray-300 rounded-xl sm:rounded-2xl p-3 sm:p-4 pl-20 sm:pl-24 text-sm sm:text-base focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all duration-200 bg-white shadow-sm"
                          placeholder="0.00"
                          required
                          disabled={paymentLoading}
                        />
                        <span className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm sm:text-base">USD</span>
                      </div>
                    </div>
                  )}

                  {/* Amount Input */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2 sm:mb-3">
                      Payment Amount *
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-base sm:text-lg font-medium">{getCurrencySymbol(paymentData.currency) || '?'}</span>
                      <input
                        type="number"
                        name="amount"
                        value={paymentData.amount}
                        onChange={handlePaymentChange}
                        step="0.01"
                        min="0.01"
                        max={selectedProject.remaining || selectedProject.budget}
                        className="w-full border border-gray-300 rounded-xl sm:rounded-2xl p-3 sm:p-4 pl-12 sm:pl-14 text-base sm:text-lg font-semibold focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all duration-200 bg-white shadow-sm"
                        placeholder="0.00"
                        required
                        disabled={paymentLoading}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1 sm:mt-2 ml-1">
                      Maximum: <span className="font-semibold text-green-600">{getCurrencySymbol(selectedProject.currency)}{parseFloat(selectedProject.remaining || selectedProject.budget).toFixed(2)}</span>
                    </p>
                  </div>

                  {/* Paid By Dropdown */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2 sm:mb-3">
                      Paid By (Employee)
                    </label>
                    <div className="relative">
                      <select
                        name="paid_by"
                        value={paymentData.paid_by}
                        onChange={handlePaymentChange}
                        className="w-full border border-gray-300 rounded-xl sm:rounded-2xl p-3 sm:p-4 focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all duration-200 bg-white shadow-sm appearance-none pr-8 sm:pr-10"
                        disabled={paymentLoading}
                      >
                        <option value="">Select Employee (Optional)</option>
                        {employees.map((employee) => (
                          <option key={employee.id} value={employee.id}>
                            {employee.name}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Note Textarea */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2 sm:mb-3">
                      Note (Optional)
                    </label>
                    <textarea
                      name="note"
                      value={paymentData.note}
                      onChange={handlePaymentChange}
                      className="w-full border border-gray-300 rounded-xl sm:rounded-2xl p-3 sm:p-4 focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all duration-200 bg-white shadow-sm resize-none"
                      placeholder="Enter payment note..."
                      rows="2"
                      disabled={paymentLoading}
                    ></textarea>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-2">
                    <button
                      type="button"
                      onClick={closePaymentModal}
                      disabled={paymentLoading}
                      className="px-4 sm:px-6 py-2.5 sm:py-3.5 border border-gray-300 text-gray-700 rounded-xl sm:rounded-2xl hover:bg-gray-50 transition-all duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:border-gray-400 order-2 sm:order-1 text-sm sm:text-base"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={paymentLoading || !paymentData.amount || parseFloat(paymentData.amount) <= 0 || !paymentData.currency}
                      className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 sm:px-6 py-2.5 sm:py-3.5 rounded-xl sm:rounded-2xl hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-md hover:shadow-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 hover:scale-105 transform order-1 sm:order-2 text-sm sm:text-base"
                    >
                      {paymentLoading ? (
                        <>
                          <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Processing...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                          </svg>
                          <span>Add Payment</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Scrollbar Styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  );
}

export default Index;